import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FileSearchOutlined,
  MinusCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { ARTHA_BIDHA } from '../constants/arthaEnums';
import { SUBANTA_SHRENI, enumToOptions } from '../constants/subantaEnums';
import { listLanguages, type Language } from '../api/languageApi';
import { listUpasargas, type Upasarga } from '../api/upasargaApi';
import {
  createTinganta,
  deleteTinganta,
  getTinganta,
  listTingantas,
  normalizeTingantaScalars,
  updateTinganta,
  TINGANTA_SCALAR_KEYS,
  type Tinganta,
  type TingantaScalars,
} from '../api/tingantaApi';
import { listNotifications, type DictionaryNotification } from '../api/notificationApi';
import {
  listStats,
  STAT_STATUS_SELECT_OPTIONS,
  type DictionaryStat,
  type StatStatus,
} from '../api/statsApi';
import {
  buildTingantaArthaPatch,
  createTingantaArtha,
  deleteTingantaArtha,
  listTingantaArthas,
  parseTingantaArthaStructuredText,
  parseTingantaArthas,
  updateTingantaArtha,
  type ParsedTingantaArthaText,
  type TingantaArtha,
} from '../api/tingantaArthaApi';
import {
  buildDerivativeArthaPatch,
  derivativeArthaCrudByKind,
  listNyantaArthas,
  listSanantaArthas,
  listYangantaArthas,
  listYanglugantaArthas,
  parseTingantaDerivativeArthas,
  type DerivativeArthaKind,
  type TingantaDerivativeArtha,
} from '../api/tingantaDerivativeArthaApi';
import {
  buildUpasargaArthaPatch,
  createUpasargaArtha,
  deleteUpasargaArtha,
  listUpasargaArthas,
  parseUpasargaArthas,
  updateUpasargaArtha,
  type UpasargaArtha,
  type UpasargaArthaFormValues,
} from '../api/upasargaArthaApi';
import { EntryNotificationsPanel } from '../components/EntryNotificationsPanel';
import { EntryMediaPanel } from '../components/EntryMediaPanel';
import { EntryStatsPanel } from '../components/EntryStatsPanel';
import { TingantaRupSections } from '../components/TingantaRupSections';

type TingantaArthaFormValues = {
  languageId: number;
  text: string;
};

const DERIVATIVE_ARTHA_SECTIONS: Record<
  DerivativeArthaKind,
  { title: string; apiPath: string; blurb: string }
> = {
  nyanta: {
    title: 'Nyanta artha (causative)',
    apiPath: 'nyanta-artha',
    blurb: 'Meanings for the causative (ण्यन्त) form of this verb, one row per language.',
  },
  sananta: {
    title: 'Sananta artha (desiderative, सनन्त)',
    apiPath: 'sananta-artha',
    blurb: 'Meanings for the desiderative (सनन्त) form, one row per language.',
  },
  yanganta: {
    title: 'Yanganta artha (frequentative, यङन्त)',
    apiPath: 'yanganta-artha',
    blurb: 'Meanings for the frequentative (यङन्त) form, one row per language.',
  },
  yangluganta: {
    title: 'Yangluganta artha (यङ्लुगन्त)',
    apiPath: 'yangluganta-artha',
    blurb: 'Meanings for the यङ्लुगन्त form, one row per language.',
  },
};

function formatUpasargaIdList(
  ids: number[],
  upasargas: Upasarga[]
): string {
  if (ids.length === 0) return '—';
  return ids
    .map((id) => {
      const u = upasargas.find((x) => x.id === id);
      return u ? `${u.name} (#${id})` : `#${id}`;
    })
    .join(' · ');
}

/** Rows in `list` whose `name` equals the parsed upasarga token (trimmed). */
function upasargasMatchingParsedName(parsedName: string, list: Upasarga[]): Upasarga[] {
  const t = parsedName.trim();
  if (!t) return [];
  return list.filter((u) => u.name.trim() === t);
}

function buildPatch(prev: Tinganta, next: TingantaScalars): Partial<TingantaScalars> {
  const patch: Partial<TingantaScalars> = {};
  for (const key of TINGANTA_SCALAR_KEYS) {
    if (prev[key] !== next[key]) {
      (patch as Record<string, unknown>)[key] = next[key];
    }
  }
  return patch;
}

function scalarsFromRecord(record: Tinganta): TingantaScalars {
  return normalizeTingantaScalars(record);
}

function tingantaSearchBlob(r: Tinganta): string {
  return [
    r.id,
    r.i,
    r.dhatu,
    r.aupadeshik,
    r.artha,
    r.artha_english,
    r.gana,
    r.pada,
    r.settva,
    r.karma,
    r.tags,
    r.baseindex,
    r.sk,
    r.artha_hindi,
  ]
    .map((x) => (x == null ? '' : String(x)).toLowerCase())
    .join('\0');
}

function strSorter(a: unknown, b: unknown): number {
  return String(a ?? '').localeCompare(String(b ?? ''), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

type TingantaNotificationListFilter = 'all' | 'unresolved' | 'any' | 'none';
/** Stats: all rows, no stat row, or at least one stat row with this verification status. */
type TingantaStatsListFilter = 'all' | 'none' | StatStatus;

const TINGANTA_NOTIF_FILTER_OPTIONS: { value: TingantaNotificationListFilter; label: string }[] = [
  { value: 'all', label: 'All (notifications)' },
  { value: 'unresolved', label: 'Has unresolved' },
  { value: 'any', label: 'Has any notification' },
  { value: 'none', label: 'No notifications' },
];

const TINGANTA_STATS_FILTER_OPTIONS: { value: TingantaStatsListFilter; label: string }[] = [
  { value: 'all', label: 'All (stats)' },
  { value: 'none', label: 'No stats' },
  ...STAT_STATUS_SELECT_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
];

function tingantaIdFromNotif(n: DictionaryNotification): number | null {
  const id = n.tingantaId;
  if (id == null) return null;
  const num = Number(id);
  return Number.isFinite(num) ? num : null;
}

function tingantaIdFromStat(s: DictionaryStat): number | null {
  const id = s.tingantaId;
  if (id == null) return null;
  const num = Number(id);
  return Number.isFinite(num) ? num : null;
}

export function TingantasPage() {
  const [rows, setRows] = useState<Tinganta[]>([]);
  const [loading, setLoading] = useState(false);
  const [listPage, setListPage] = useState(1);
  const [listPageSize, setListPageSize] = useState(10);
  const [listSearch, setListSearch] = useState('');
  const [notifListForFilter, setNotifListForFilter] = useState<DictionaryNotification[]>([]);
  const [statListForFilter, setStatListForFilter] = useState<DictionaryStat[]>([]);
  const [filterNotifications, setFilterNotifications] =
    useState<TingantaNotificationListFilter>('all');
  const [filterStats, setFilterStats] = useState<TingantaStatsListFilter>('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Tinganta | null>(null);
  const [saving, setSaving] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<Tinganta | null>(null);
  const [form] = Form.useForm<TingantaScalars>();
  const [languageRows, setLanguageRows] = useState<Language[]>([]);
  const [tingantaArthaRows, setTingantaArthaRows] = useState<TingantaArtha[]>([]);
  const [tingantaArthasLoading, setTingantaArthasLoading] = useState(false);
  const [taModalOpen, setTaModalOpen] = useState(false);
  const [taEditing, setTaEditing] = useState<TingantaArtha | null>(null);
  const [taHeadTingantaId, setTaHeadTingantaId] = useState<number | null>(null);
  const [taModalSaving, setTaModalSaving] = useState(false);
  const [taForm] = Form.useForm<TingantaArthaFormValues>();
  const [taParseModalOpen, setTaParseModalOpen] = useState(false);
  const [taParsed, setTaParsed] = useState<ParsedTingantaArthaText | null>(null);
  const [taParseSelectDifferent, setTaParseSelectDifferent] = useState(false);
  const [taParseDelete, setTaParseDelete] = useState(false);
  const [taParseSourceArtha, setTaParseSourceArtha] = useState<TingantaArtha | null>(null);
  const [taParseSaving, setTaParseSaving] = useState(false);
  const [taParseExtraForm] = Form.useForm<{ manualUpasargaIds: number[] }>();
  const [upasargaList, setUpasargaList] = useState<Upasarga[]>([]);
  const [upasargaArthaRows, setUpasargaArthaRows] = useState<UpasargaArtha[]>([]);
  const [upasargaArthasLoading, setUpasargaArthasLoading] = useState(false);
  const [uaModalOpen, setUaModalOpen] = useState(false);
  const [uaEditing, setUaEditing] = useState<UpasargaArtha | null>(null);
  const [uaHeadTingantaId, setUaHeadTingantaId] = useState<number | null>(null);
  const [uaModalSaving, setUaModalSaving] = useState(false);
  const [uaForm] = Form.useForm<UpasargaArthaFormValues>();
  const [nyantaArthaRows, setNyantaArthaRows] = useState<TingantaDerivativeArtha[]>([]);
  const [nyantaArthasLoading, setNyantaArthasLoading] = useState(false);
  const [sanantaArthaRows, setSanantaArthaRows] = useState<TingantaDerivativeArtha[]>([]);
  const [sanantaArthasLoading, setSanantaArthasLoading] = useState(false);
  const [yangantaArthaRows, setYangantaArthaRows] = useState<TingantaDerivativeArtha[]>([]);
  const [yangantaArthasLoading, setYangantaArthasLoading] = useState(false);
  const [yanglugantaArthaRows, setYanglugantaArthaRows] = useState<TingantaDerivativeArtha[]>([]);
  const [yanglugantaArthasLoading, setYanglugantaArthasLoading] = useState(false);
  const [derArthaModalOpen, setDerArthaModalOpen] = useState(false);
  const [derArthaKind, setDerArthaKind] = useState<DerivativeArthaKind>('nyanta');
  const [derArthaEditing, setDerArthaEditing] = useState<TingantaDerivativeArtha | null>(null);
  const [derArthaHeadTingantaId, setDerArthaHeadTingantaId] = useState<number | null>(null);
  const [derArthaModalSaving, setDerArthaModalSaving] = useState(false);
  const [derArthaForm] = Form.useForm<TingantaArthaFormValues>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [data, notifs, statsRows] = await Promise.all([
        listTingantas(),
        listNotifications(),
        listStats(),
      ]);
      setRows(data);
      setNotifListForFilter(notifs);
      setStatListForFilter(statsRows);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Failed to load tingantas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const unresolvedNotifTingantaIds = useMemo(() => {
    const s = new Set<number>();
    for (const n of notifListForFilter) {
      const tid = tingantaIdFromNotif(n);
      if (tid != null && !n.resolved) s.add(tid);
    }
    return s;
  }, [notifListForFilter]);

  const anyNotifTingantaIds = useMemo(() => {
    const s = new Set<number>();
    for (const n of notifListForFilter) {
      const tid = tingantaIdFromNotif(n);
      if (tid != null) s.add(tid);
    }
    return s;
  }, [notifListForFilter]);

  const statsTingantaIds = useMemo(() => {
    const s = new Set<number>();
    for (const st of statListForFilter) {
      const tid = tingantaIdFromStat(st);
      if (tid != null) s.add(tid);
    }
    return s;
  }, [statListForFilter]);

  const tingantaIdsByStatStatus = useMemo(() => {
    const sets: Record<StatStatus, Set<number>> = {
      notverified: new Set(),
      verifiedonce: new Set(),
      verifiedwtice: new Set(),
      verifiedthrice: new Set(),
    };
    for (const row of statListForFilter) {
      const tid = tingantaIdFromStat(row);
      if (tid == null) continue;
      const bucket = sets[row.status];
      if (bucket) bucket.add(tid);
    }
    return sets;
  }, [statListForFilter]);

  const filteredTingantaRows = useMemo(() => {
    let out = rows;
    const q = listSearch.trim().toLowerCase();
    if (q) {
      out = out.filter((r) => tingantaSearchBlob(r).includes(q));
    }
    if (filterNotifications === 'unresolved') {
      out = out.filter((r) => unresolvedNotifTingantaIds.has(r.id));
    } else if (filterNotifications === 'any') {
      out = out.filter((r) => anyNotifTingantaIds.has(r.id));
    } else if (filterNotifications === 'none') {
      out = out.filter((r) => !anyNotifTingantaIds.has(r.id));
    }
    if (filterStats === 'none') {
      out = out.filter((r) => !statsTingantaIds.has(r.id));
    } else if (filterStats !== 'all') {
      const status: StatStatus = filterStats;
      out = out.filter((r) => tingantaIdsByStatStatus[status].has(r.id));
    }
    return out;
  }, [
    rows,
    listSearch,
    filterNotifications,
    filterStats,
    unresolvedNotifTingantaIds,
    anyNotifTingantaIds,
    statsTingantaIds,
    tingantaIdsByStatStatus,
  ]);

  const listTotalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredTingantaRows.length / listPageSize)),
    [filteredTingantaRows.length, listPageSize]
  );

  const listPageOptions = useMemo(
    () =>
      Array.from({ length: listTotalPages }, (_, idx) => ({
        value: idx + 1,
        label: `Page ${idx + 1}`,
      })),
    [listTotalPages]
  );

  useEffect(() => {
    setListPage(1);
  }, [listSearch, filterNotifications, filterStats]);

  useEffect(() => {
    setListPage((p) => (p > listTotalPages ? listTotalPages : p));
  }, [listTotalPages]);

  useEffect(() => {
    void listLanguages()
      .then(setLanguageRows)
      .catch((err: unknown) => {
        const ax = err as { response?: { data?: { message?: string } }; message?: string };
        message.error(ax?.response?.data?.message || ax?.message || 'Failed to load languages');
      });
  }, []);

  useEffect(() => {
    void listUpasargas()
      .then(setUpasargaList)
      .catch((err: unknown) => {
        const ax = err as { response?: { data?: { message?: string } }; message?: string };
        message.error(ax?.response?.data?.message || ax?.message || 'Failed to load upasargas');
      });
  }, []);

  useEffect(() => {
    if (!detail) {
      setTingantaArthaRows([]);
      setTingantaArthasLoading(false);
      return;
    }
    const embedded = parseTingantaArthas(detail.tingantaArthas).filter(
      (a) => a.tingantaId === detail.id
    );
    if (embedded.length > 0) {
      setTingantaArthaRows(embedded);
      setTingantaArthasLoading(false);
      return;
    }
    let cancelled = false;
    setTingantaArthasLoading(true);
    void listTingantaArthas(detail.id)
      .then((rows) => {
        if (!cancelled) setTingantaArthaRows(rows);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const ax = err as { response?: { data?: { message?: string } }; message?: string };
          message.error(
            ax?.response?.data?.message || ax?.message || 'Failed to load tinganta arthas'
          );
          setTingantaArthaRows([]);
        }
      })
      .finally(() => {
        if (!cancelled) setTingantaArthasLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [detail]);

  useEffect(() => {
    if (!detail) {
      setNyantaArthaRows([]);
      setNyantaArthasLoading(false);
      return;
    }
    const embedded = parseTingantaDerivativeArthas(detail.nyantaArthas as unknown[] | undefined).filter(
      (a) => a.tingantaId === detail.id
    );
    if (embedded.length > 0) {
      setNyantaArthaRows(embedded);
      setNyantaArthasLoading(false);
      return;
    }
    let cancelled = false;
    setNyantaArthasLoading(true);
    void listNyantaArthas(detail.id)
      .then((rows) => {
        if (!cancelled) setNyantaArthaRows(rows);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const ax = err as { response?: { data?: { message?: string } }; message?: string };
          message.error(
            ax?.response?.data?.message || ax?.message || 'Failed to load nyanta arthas'
          );
          setNyantaArthaRows([]);
        }
      })
      .finally(() => {
        if (!cancelled) setNyantaArthasLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [detail]);

  useEffect(() => {
    if (!detail) {
      setSanantaArthaRows([]);
      setSanantaArthasLoading(false);
      return;
    }
    const embedded = parseTingantaDerivativeArthas(
      detail.sanantaArthas as unknown[] | undefined
    ).filter((a) => a.tingantaId === detail.id);
    if (embedded.length > 0) {
      setSanantaArthaRows(embedded);
      setSanantaArthasLoading(false);
      return;
    }
    let cancelled = false;
    setSanantaArthasLoading(true);
    void listSanantaArthas(detail.id)
      .then((rows) => {
        if (!cancelled) setSanantaArthaRows(rows);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const ax = err as { response?: { data?: { message?: string } }; message?: string };
          message.error(
            ax?.response?.data?.message || ax?.message || 'Failed to load sananta arthas'
          );
          setSanantaArthaRows([]);
        }
      })
      .finally(() => {
        if (!cancelled) setSanantaArthasLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [detail]);

  useEffect(() => {
    if (!detail) {
      setYangantaArthaRows([]);
      setYangantaArthasLoading(false);
      return;
    }
    const embedded = parseTingantaDerivativeArthas(
      detail.yangantaArthas as unknown[] | undefined
    ).filter((a) => a.tingantaId === detail.id);
    if (embedded.length > 0) {
      setYangantaArthaRows(embedded);
      setYangantaArthasLoading(false);
      return;
    }
    let cancelled = false;
    setYangantaArthasLoading(true);
    void listYangantaArthas(detail.id)
      .then((rows) => {
        if (!cancelled) setYangantaArthaRows(rows);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const ax = err as { response?: { data?: { message?: string } }; message?: string };
          message.error(
            ax?.response?.data?.message || ax?.message || 'Failed to load yanganta arthas'
          );
          setYangantaArthaRows([]);
        }
      })
      .finally(() => {
        if (!cancelled) setYangantaArthasLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [detail]);

  useEffect(() => {
    if (!detail) {
      setYanglugantaArthaRows([]);
      setYanglugantaArthasLoading(false);
      return;
    }
    const embedded = parseTingantaDerivativeArthas(
      detail.yanglugantaArthas as unknown[] | undefined
    ).filter((a) => a.tingantaId === detail.id);
    if (embedded.length > 0) {
      setYanglugantaArthaRows(embedded);
      setYanglugantaArthasLoading(false);
      return;
    }
    let cancelled = false;
    setYanglugantaArthasLoading(true);
    void listYanglugantaArthas(detail.id)
      .then((rows) => {
        if (!cancelled) setYanglugantaArthaRows(rows);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const ax = err as { response?: { data?: { message?: string } }; message?: string };
          message.error(
            ax?.response?.data?.message || ax?.message || 'Failed to load yangluganta arthas'
          );
          setYanglugantaArthaRows([]);
        }
      })
      .finally(() => {
        if (!cancelled) setYanglugantaArthasLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [detail]);

  useEffect(() => {
    if (!detail) {
      setUpasargaArthaRows([]);
      setUpasargaArthasLoading(false);
      return;
    }
    const embedded = parseUpasargaArthas(detail.upasargaArthas as unknown[] | undefined).filter(
      (a) => a.tingantaId === detail.id
    );
    if (embedded.length > 0) {
      setUpasargaArthaRows(embedded);
      setUpasargaArthasLoading(false);
      return;
    }
    let cancelledUa = false;
    setUpasargaArthasLoading(true);
    void listUpasargaArthas(detail.id)
      .then((rows) => {
        if (!cancelledUa) setUpasargaArthaRows(rows);
      })
      .catch((err: unknown) => {
        if (!cancelledUa) {
          const ax = err as { response?: { data?: { message?: string } }; message?: string };
          message.error(
            ax?.response?.data?.message || ax?.message || 'Failed to load upasarga arthas'
          );
          setUpasargaArthaRows([]);
        }
      })
      .finally(() => {
        if (!cancelledUa) setUpasargaArthasLoading(false);
      });
    return () => {
      cancelledUa = true;
    };
  }, [detail]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue(normalizeTingantaScalars({}));
    setModalOpen(true);
  };

  const openEdit = (record: Tinganta) => {
    setEditing(record);
    form.setFieldsValue(scalarsFromRecord(record));
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      const scalars = normalizeTingantaScalars(values);
      setSaving(true);
      if (editing) {
        const patch = buildPatch(editing, scalars);
        if (Object.keys(patch).length === 0) {
          message.info('No changes to save');
          closeModal();
          return;
        }
        await updateTinganta(editing.id, patch);
        message.success('Tinganta updated');
      } else {
        await createTinganta(scalars);
        message.success('Tinganta created');
      }
      closeModal();
      await load();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (id: number) => {
    setDetailLoading(true);
    setDetailOpen(true);
    setDetail(null);
    try {
      const row = await getTinganta(id);
      setDetail(row);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Failed to load tinganta');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteTinganta(id);
      message.success('Tinganta deleted');
      await load();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Delete failed');
    }
  };

  const reloadDetailIfOpen = useCallback(async () => {
    const id = detail?.id;
    if (id == null) return;
    try {
      const row = await getTinganta(id);
      setDetail(row);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Failed to refresh tinganta');
    }
  }, [detail?.id]);

  const closeTaModal = useCallback(() => {
    setTaModalOpen(false);
    setTaEditing(null);
    setTaHeadTingantaId(null);
    taForm.resetFields();
  }, [taForm]);

  const openTaCreate = useCallback(
    (tingantaId: number) => {
      if (languageRows.length === 0) {
        message.warning('No languages loaded. Add languages on the Languages page first.');
        return;
      }
      setTaEditing(null);
      setTaHeadTingantaId(tingantaId);
      taForm.resetFields();
      taForm.setFieldsValue({
        languageId: languageRows[0].id,
        text: '',
      });
      setTaModalOpen(true);
    },
    [languageRows, taForm]
  );

  const openTaEdit = useCallback(
    (row: TingantaArtha) => {
      setTaEditing(row);
      setTaHeadTingantaId(row.tingantaId);
      taForm.setFieldsValue({
        languageId: row.languageId,
        text: row.text,
      });
      setTaModalOpen(true);
    },
    [taForm]
  );

  const openTaParseView = useCallback(
    (row: TingantaArtha) => {
      setTaParsed(parseTingantaArthaStructuredText(row.text));
      setTaParseSourceArtha(row);
      setTaParseSelectDifferent(false);
      setTaParseDelete(false);
      setTaParseSaving(false);
      taParseExtraForm.resetFields();
      setTaParseModalOpen(true);
    },
    [taParseExtraForm]
  );

  const closeTaParseView = useCallback(() => {
    setTaParseModalOpen(false);
    setTaParsed(null);
    setTaParseSourceArtha(null);
    setTaParseSelectDifferent(false);
    setTaParseDelete(false);
    setTaParseSaving(false);
    taParseExtraForm.resetFields();
  }, [taParseExtraForm]);

  const taParseUpasargaMatches = useMemo(() => {
    if (!taParsed?.upasarga?.trim()) return [] as Upasarga[];
    return upasargasMatchingParsedName(taParsed.upasarga, upasargaList);
  }, [taParsed, upasargaList]);

  const handleTaParseDone = useCallback(async () => {
    const source = taParseSourceArtha;
    const parsed = taParsed;
    if (!source || !parsed) return;

    let selectedUpasargaOrder: number[];
    if (taParseSelectDifferent) {
      const raw = taParseExtraForm.getFieldValue('manualUpasargaIds') as unknown;
      const arr = Array.isArray(raw) ? raw : [];
      selectedUpasargaOrder = arr.filter((id): id is number => typeof id === 'number');
    } else {
      selectedUpasargaOrder = taParseUpasargaMatches.map((u) => u.id);
    }
    const matchedUpasargaIds = taParseUpasargaMatches.map((u) => u.id);
    const primaryUpasargaId = selectedUpasargaOrder[0] ?? matchedUpasargaIds[0];

    if (primaryUpasargaId == null) {
      message.error(
        'No upasarga id: ensure the parsed name matches an upasarga row or choose one under Select different.'
      );
      return;
    }

    const meanings = parsed.meanings.map((m) => m.trim()).filter((m) => m.length > 0);
    if (meanings.length === 0) {
      message.warning('No meanings to insert.');
      return;
    }

    setTaParseSaving(true);
    try {
      const { tingantaId, languageId } = source;
      for (const artha of meanings) {
        await createUpasargaArtha({
          tingantaId,
          languageId,
          artha,
          upasargaIds: [primaryUpasargaId],
        });
      }
      if (taParseDelete) {
        await deleteTingantaArtha(source.id);
        message.success('Upasarga arthas created and tinganta artha removed.');
      } else {
        message.success(
          `Created ${meanings.length} upasarga ${meanings.length === 1 ? 'artha' : 'arthas'}.`
        );
      }
      closeTaParseView();
      await reloadDetailIfOpen();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Operation failed');
    } finally {
      setTaParseSaving(false);
    }
  }, [
    closeTaParseView,
    reloadDetailIfOpen,
    taParseDelete,
    taParseExtraForm,
    taParseSelectDifferent,
    taParsed,
    taParseSourceArtha,
    taParseUpasargaMatches,
  ]);

  const submitTaModal = useCallback(async () => {
    try {
      const values = await taForm.validateFields();
      const headId = taHeadTingantaId;
      if (headId == null) return;
      setTaModalSaving(true);
      const next = { languageId: values.languageId, text: values.text.trim() };
      if (taEditing) {
        const patch = buildTingantaArthaPatch(taEditing, next);
        if (Object.keys(patch).length === 0) {
          message.info('No changes to save');
          closeTaModal();
          return;
        }
        await updateTingantaArtha(taEditing.id, patch);
        message.success('Tinganta artha updated');
      } else {
        await createTingantaArtha({
          tingantaId: headId,
          languageId: next.languageId,
          text: next.text,
        });
        message.success('Tinganta artha created');
      }
      closeTaModal();
      await reloadDetailIfOpen();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Save failed');
    } finally {
      setTaModalSaving(false);
    }
  }, [closeTaModal, reloadDetailIfOpen, taEditing, taForm, taHeadTingantaId]);

  const handleDeleteTingantaArtha = useCallback(
    async (id: number) => {
      try {
        await deleteTingantaArtha(id);
        message.success('Tinganta artha deleted');
        await reloadDetailIfOpen();
      } catch (err: unknown) {
        const ax = err as { response?: { data?: { message?: string } }; message?: string };
        message.error(ax?.response?.data?.message || ax?.message || 'Delete failed');
      }
    },
    [reloadDetailIfOpen]
  );

  const closeDerArthaModal = useCallback(() => {
    setDerArthaModalOpen(false);
    setDerArthaEditing(null);
    setDerArthaHeadTingantaId(null);
    derArthaForm.resetFields();
  }, [derArthaForm]);

  const openDerivativeArthaCreate = useCallback(
    (kind: DerivativeArthaKind, tingantaId: number) => {
      if (languageRows.length === 0) {
        message.warning('No languages loaded. Add languages on the Languages page first.');
        return;
      }
      setDerArthaKind(kind);
      setDerArthaEditing(null);
      setDerArthaHeadTingantaId(tingantaId);
      derArthaForm.resetFields();
      derArthaForm.setFieldsValue({
        languageId: languageRows[0].id,
        text: '',
      });
      setDerArthaModalOpen(true);
    },
    [derArthaForm, languageRows]
  );

  const openDerivativeArthaEdit = useCallback(
    (kind: DerivativeArthaKind, row: TingantaDerivativeArtha) => {
      setDerArthaKind(kind);
      setDerArthaEditing(row);
      setDerArthaHeadTingantaId(row.tingantaId);
      derArthaForm.setFieldsValue({ languageId: row.languageId, text: row.text });
      setDerArthaModalOpen(true);
    },
    [derArthaForm]
  );

  const handleDeleteDerivativeArtha = useCallback(
    async (kind: DerivativeArthaKind, id: number) => {
      try {
        await derivativeArthaCrudByKind[kind].delete(id);
        message.success(`Deleted (${DERIVATIVE_ARTHA_SECTIONS[kind].apiPath})`);
        await reloadDetailIfOpen();
      } catch (err: unknown) {
        const ax = err as { response?: { data?: { message?: string } }; message?: string };
        message.error(ax?.response?.data?.message || ax?.message || 'Delete failed');
      }
    },
    [reloadDetailIfOpen]
  );

  const derivativeArthaColumnsFor = useCallback(
    (kind: DerivativeArthaKind): ColumnsType<TingantaDerivativeArtha> => [
      { title: 'ID', dataIndex: 'id', width: 64 },
      {
        title: 'Language',
        key: 'lang',
        width: 160,
        ellipsis: true,
        render: (_, r) => {
          const nested = r.language;
          if (nested && typeof nested.name === 'string') {
            return `${nested.name} (${nested.code})`;
          }
          const L = languageRows.find((l) => l.id === r.languageId);
          return L ? `${L.name} (${L.code})` : `#${r.languageId}`;
        },
      },
      { title: 'Text', dataIndex: 'text', ellipsis: true },
      {
        title: 'Actions',
        key: 'derActions',
        width: 160,
        fixed: 'right',
        render: (_, r) => (
          <Space size="small" wrap>
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => openDerivativeArthaEdit(kind, r)}
            >
              Edit
            </Button>
            <Popconfirm
              title={`Delete this row (${DERIVATIVE_ARTHA_SECTIONS[kind].apiPath})?`}
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={() => void handleDeleteDerivativeArtha(kind, r.id)}
            >
              <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                Delete
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [handleDeleteDerivativeArtha, languageRows, openDerivativeArthaEdit]
  );

  const submitDerArthaModal = useCallback(async () => {
    try {
      const values = await derArthaForm.validateFields();
      const headId = derArthaHeadTingantaId;
      if (headId == null) return;
      setDerArthaModalSaving(true);
      const next = { languageId: values.languageId, text: values.text.trim() };
      const api = derivativeArthaCrudByKind[derArthaKind];
      if (derArthaEditing) {
        const patch = buildDerivativeArthaPatch(derArthaEditing, next);
        if (Object.keys(patch).length === 0) {
          message.info('No changes to save');
          closeDerArthaModal();
          return;
        }
        await api.update(derArthaEditing.id, patch);
        message.success(`Updated (${DERIVATIVE_ARTHA_SECTIONS[derArthaKind].apiPath})`);
      } else {
        await api.create({
          tingantaId: headId,
          languageId: next.languageId,
          text: next.text,
        });
        message.success(`Created (${DERIVATIVE_ARTHA_SECTIONS[derArthaKind].apiPath})`);
      }
      closeDerArthaModal();
      await reloadDetailIfOpen();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Save failed');
    } finally {
      setDerArthaModalSaving(false);
    }
  }, [
    closeDerArthaModal,
    derArthaEditing,
    derArthaForm,
    derArthaHeadTingantaId,
    derArthaKind,
    reloadDetailIfOpen,
  ]);

  const closeUaModal = useCallback(() => {
    setUaModalOpen(false);
    setUaEditing(null);
    setUaHeadTingantaId(null);
    uaForm.resetFields();
  }, [uaForm]);

  const openUaCreate = useCallback(
    (tingantaId: number) => {
      if (languageRows.length === 0) {
        message.warning('No languages loaded. Add languages on the Languages page first.');
        return;
      }
      if (upasargaList.length === 0) {
        message.warning('No upasargas loaded. Add upasargas on the Upasarga page first.');
        return;
      }
      setUaEditing(null);
      setUaHeadTingantaId(tingantaId);
      uaForm.resetFields();
      uaForm.setFieldsValue({
        languageId: languageRows[0].id,
        artha: '',
        upasargaIds: [],
        example: '',
        antonyms: '',
        synonyms: '',
        tags: '',
      });
      setUaModalOpen(true);
    },
    [languageRows, upasargaList.length, uaForm]
  );

  const openUaEdit = useCallback(
    (row: UpasargaArtha) => {
      setUaEditing(row);
      setUaHeadTingantaId(row.tingantaId);
      uaForm.setFieldsValue({
        languageId: row.languageId,
        artha: row.artha,
        upasargaIds: [...row.upasargaIds],
        example: row.example ?? '',
        antonyms: row.antonyms ?? '',
        synonyms: row.synonyms ?? '',
        tags: row.tags ?? '',
        bidha: row.bidha ?? undefined,
        prakar: row.prakar ?? undefined,
      });
      setUaModalOpen(true);
    },
    [uaForm]
  );

  const submitUaModal = useCallback(async () => {
    try {
      const values = await uaForm.validateFields();
      const headId = uaHeadTingantaId;
      if (headId == null) return;
      const upasargaIdsNorm = (values.upasargaIds ?? [])
        .map((id) => Number(id))
        .filter(Number.isFinite);
      const valuesNorm: UpasargaArthaFormValues = {
        ...values,
        upasargaIds: upasargaIdsNorm,
      };
      setUaModalSaving(true);
      if (uaEditing) {
        const patch = buildUpasargaArthaPatch(uaEditing, valuesNorm);
        if (Object.keys(patch).length === 0) {
          message.info('No changes to save');
          closeUaModal();
          return;
        }
        await updateUpasargaArtha(uaEditing.id, patch);
        message.success('Upasarga artha updated');
      } else {
        const optStr = (s: string | undefined): string | undefined => {
          const t = (s ?? '').trim();
          return t === '' ? undefined : t;
        };
        await createUpasargaArtha({
          tingantaId: headId,
          languageId: valuesNorm.languageId,
          artha: (valuesNorm.artha ?? '').trim(),
          upasargaIds: valuesNorm.upasargaIds,
          example: optStr(valuesNorm.example),
          antonyms: optStr(valuesNorm.antonyms),
          synonyms: optStr(valuesNorm.synonyms),
          tags: optStr(valuesNorm.tags),
          bidha: valuesNorm.bidha ?? undefined,
          prakar: valuesNorm.prakar ?? undefined,
        });
        message.success('Upasarga artha created');
      }
      closeUaModal();
      await reloadDetailIfOpen();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Save failed');
    } finally {
      setUaModalSaving(false);
    }
  }, [closeUaModal, reloadDetailIfOpen, uaEditing, uaForm, uaHeadTingantaId]);

  const handleDeleteUpasargaArtha = useCallback(
    async (id: number) => {
      try {
        await deleteUpasargaArtha(id);
        message.success('Upasarga artha deleted');
        await reloadDetailIfOpen();
      } catch (err: unknown) {
        const ax = err as { response?: { data?: { message?: string } }; message?: string };
        message.error(ax?.response?.data?.message || ax?.message || 'Delete failed');
      }
    },
    [reloadDetailIfOpen]
  );

  const tingantaArthaColumns: ColumnsType<TingantaArtha> = useMemo(
    () => [
      { title: 'ID', dataIndex: 'id', width: 64 },
      {
        title: 'Language',
        key: 'lang',
        width: 160,
        ellipsis: true,
        render: (_, r) => {
          const nested = r.language;
          if (nested && typeof nested.name === 'string') {
            return `${nested.name} (${nested.code})`;
          }
          const L = languageRows.find((l) => l.id === r.languageId);
          return L ? `${L.name} (${L.code})` : `#${r.languageId}`;
        },
      },
      { title: 'Text', dataIndex: 'text', ellipsis: true },
      {
        title: 'Actions',
        key: 'taActions',
        width: 240,
        fixed: 'right',
        render: (_, r) => (
          <Space size="small" wrap>
            <Button
              type="link"
              size="small"
              icon={<FileSearchOutlined />}
              onClick={() => openTaParseView(r)}
            >
              Parse
            </Button>
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openTaEdit(r)}>
              Edit
            </Button>
            <Popconfirm
              title="Delete this tinganta artha?"
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={() => void handleDeleteTingantaArtha(r.id)}
            >
              <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                Delete
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [handleDeleteTingantaArtha, languageRows, openTaEdit, openTaParseView]
  );

  const languageSelectOptions = useMemo(
    () => languageRows.map((l) => ({ value: l.id, label: `${l.name} (${l.code})` })),
    [languageRows]
  );

  const bidhaOpts = useMemo(() => enumToOptions(ARTHA_BIDHA), []);
  const prakarOpts = useMemo(() => enumToOptions(SUBANTA_SHRENI), []);
  const upasargaSelectOptions = useMemo(
    () =>
      [...upasargaList].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
      ).map((u) => ({ value: u.id, label: `${u.name} (#${u.id})` })),
    [upasargaList]
  );

  const upasargaArthaColumns: ColumnsType<UpasargaArtha> = useMemo(
    () => [
      { title: 'ID', dataIndex: 'id', width: 64 },
      {
        title: 'Language',
        key: 'lang',
        width: 140,
        ellipsis: true,
        render: (_, r) => {
          const nested = r.language;
          if (nested && typeof nested.name === 'string') {
            return `${nested.name} (${nested.code})`;
          }
          const L = languageRows.find((l) => l.id === r.languageId);
          return L ? `${L.name} (${L.code})` : `#${r.languageId}`;
        },
      },
      {
        title: 'Upasargas (order)',
        key: 'upa',
        width: 200,
        ellipsis: true,
        render: (_, r) => formatUpasargaIdList(r.upasargaIds, upasargaList),
      },
      { title: 'Artha', dataIndex: 'artha', ellipsis: true },
      { title: 'Bidha', dataIndex: 'bidha', width: 100, ellipsis: true },
      { title: 'Prakār', dataIndex: 'prakar', width: 90, ellipsis: true },
      {
        title: 'Actions',
        key: 'uaActions',
        width: 160,
        fixed: 'right',
        render: (_, r) => (
          <Space size="small">
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openUaEdit(r)}>
              Edit
            </Button>
            <Popconfirm
              title="Delete this upasarga artha?"
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={() => void handleDeleteUpasargaArtha(r.id)}
            >
              <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                Delete
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [handleDeleteUpasargaArtha, languageRows, openUaEdit, upasargaList]
  );

  const requiredStr = (label: string) => [
    { required: true, message: `${label} is required` },
  ];

  const columns: ColumnsType<Tinganta> = [
    {
      title: 'ID',
      dataIndex: 'i',
      width: 100,
      sorter: (a, b) => strSorter(a.i, b.i),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Dhātu',
      dataIndex: 'dhatu',
      width: 100,
      ellipsis: true,
      sorter: (a, b) => strSorter(a.dhatu, b.dhatu),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Aupadeśik',
      dataIndex: 'aupadeshik',
      width: 120,
      ellipsis: true,
      sorter: (a, b) => strSorter(a.aupadeshik, b.aupadeshik),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Artha',
      dataIndex: 'artha',
      ellipsis: true,
      width: 400,
      sorter: (a, b) => strSorter(a.artha, b.artha),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Gaṇa',
      dataIndex: 'gana',
      width: 100,
      ellipsis: true,
      sorter: (a, b) => strSorter(a.gana, b.gana),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Pada',
      dataIndex: 'pada',
      width: 130,
      ellipsis: true,
      sorter: (a, b) => strSorter(a.pada, b.pada),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Settva',
      dataIndex: 'settva',
      width: 130,
      ellipsis: true,
      sorter: (a, b) => strSorter(a.settva, b.settva),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Karma',
      dataIndex: 'karma',
      width: 130,
      ellipsis: true,
      sorter: (a, b) => strSorter(a.karma, b.karma),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 240,
      render: (_, record) => (
        <Space size="small" wrap>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => void openDetail(record.id)}
          >
          </Button>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>
          </Button>
          <Popconfirm
            title="Delete this tinganta?"
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => void handleDelete(record.id)}
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Tinganta"
      extra={
        <Space wrap align="center">
          <Input.Search
            allowClear
            placeholder="Search dhātu, artha, tags, id…"
            style={{ width: 280 }}
            value={listSearch}
            onChange={(e) => setListSearch(e.target.value)}
          />
          <Select<TingantaNotificationListFilter>
            value={filterNotifications}
            onChange={setFilterNotifications}
            options={TINGANTA_NOTIF_FILTER_OPTIONS}
            style={{ width: 200 }}
            popupMatchSelectWidth={false}
          />
          <Select<TingantaStatsListFilter>
            value={filterStats}
            onChange={setFilterStats}
            options={TINGANTA_STATS_FILTER_OPTIONS}
            style={{ width: 200 }}
            popupMatchSelectWidth={false}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Add tinganta
          </Button>
        </Space>
      }
    >
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        CRUD for <code>/tinganta</code> (verb forms) on the dictionary API (
        {process.env.REACT_APP_DICTIONARY_API_URL || '—'}). Notification and stats filters use{' '}
        <code>/notifications</code> and <code>/stats</code> (loaded with the list).
      </Typography.Paragraph>
      <Table<Tinganta>
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={filteredTingantaRows}
        pagination={{
          current: listPage,
          pageSize: listPageSize,
          total: filteredTingantaRows.length,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          showTotal: (total, range) => (
            <Space size={8} wrap>
              <Typography.Text type="secondary">
                {total ? `${range[0]}-${range[1]} of ${total}` : '0'}
              </Typography.Text>
              <Typography.Text type="secondary">Go to</Typography.Text>
              <Select
                size="small"
                style={{ width: 128 }}
                value={listPage}
                options={listPageOptions}
                showSearch
                optionFilterProp="label"
                onChange={(value) => setListPage(value)}
              />
            </Space>
          ),
          onChange: (p, ps) => {
            setListPage(p);
            setListPageSize(ps);
          },
        }}
        scroll={{ x: 960 }}
      />

      <Modal
        title="Tinganta details"
        open={detailOpen}
        onCancel={() => {
          setDetailOpen(false);
          setDetail(null);
        }}
        footer={
          detail && !detailLoading ? (
            <Space style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
              <Button
                onClick={() => {
                  setDetailOpen(false);
                  setDetail(null);
                }}
              >
                Close
              </Button>
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => {
                  const d = detail;
                  setDetailOpen(false);
                  setDetail(null);
                  openEdit(d);
                }}
              >
                Edit
              </Button>
            </Space>
          ) : null
        }
        width={920}
        styles={{ body: { maxHeight: '85vh', overflowY: 'auto' } }}
      >
        {detailLoading ? (
          <Typography.Text type="secondary">Loading…</Typography.Text>
        ) : detail ? (
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <Typography.Text>
              <strong>ID:</strong> {detail.id}
            </Typography.Text>

            <EntryStatsPanel
              variant="tinganta"
              parentId={detail.id}
              rawStats={detail.stats}
              onReload={reloadDetailIfOpen}
            />

            <EntryNotificationsPanel
              variant="tinganta"
              parentId={detail.id}
              rawNotifications={detail.notifications}
              onReload={reloadDetailIfOpen}
            />

            {TINGANTA_SCALAR_KEYS.map((key) => (
              <Typography.Paragraph key={key} style={{ marginBottom: 4 }}>
                <Typography.Text strong>{key}:</Typography.Text> {detail[key] || '—'}
              </Typography.Paragraph>
            ))}

            <Typography.Title level={5} style={{ marginTop: 16, marginBottom: 8 }}>
              Tiṅganta arthas (per language)
            </Typography.Title>
            <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
              <code>GET/POST /tinganta-artha</code> — one row per language for this entry.
            </Typography.Paragraph>
            <Space style={{ marginBottom: 8 }}>
              <Button
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => openTaCreate(detail.id)}
              >
                Add tinganta artha
              </Button>
            </Space>
            {tingantaArthaRows.length > 0 ? (
              <Table<TingantaArtha>
                size="small"
                rowKey="id"
                loading={tingantaArthasLoading}
                columns={tingantaArthaColumns}
                dataSource={tingantaArthaRows}
                pagination={false}
                scroll={{ x: 640 }}
              />
            ) : (
              <Typography.Paragraph type="secondary">
                {tingantaArthasLoading ? 'Loading…' : 'none'}
              </Typography.Paragraph>
            )}

            {(
              [
                {
                  kind: 'nyanta' as const,
                  rows: nyantaArthaRows,
                  loading: nyantaArthasLoading,
                  addLabel: 'Add nyanta artha',
                },
                {
                  kind: 'sananta' as const,
                  rows: sanantaArthaRows,
                  loading: sanantaArthasLoading,
                  addLabel: 'Add sananta artha',
                },
                {
                  kind: 'yanganta' as const,
                  rows: yangantaArthaRows,
                  loading: yangantaArthasLoading,
                  addLabel: 'Add yanganta artha',
                },
                {
                  kind: 'yangluganta' as const,
                  rows: yanglugantaArthaRows,
                  loading: yanglugantaArthasLoading,
                  addLabel: 'Add yangluganta artha',
                },
              ] as const
            ).map(({ kind, rows, loading, addLabel }) => {
              const meta = DERIVATIVE_ARTHA_SECTIONS[kind];
              return (
                <React.Fragment key={kind}>
                  <Typography.Title level={5} style={{ marginTop: 16, marginBottom: 8 }}>
                    {meta.title}
                  </Typography.Title>
                  <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
                    <code>
                      GET/POST /{meta.apiPath}
                    </code>{' '}
                    — {meta.blurb}
                  </Typography.Paragraph>
                  <Space style={{ marginBottom: 8 }}>
                    <Button
                      type="primary"
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={() => openDerivativeArthaCreate(kind, detail.id)}
                    >
                      {addLabel}
                    </Button>
                  </Space>
                  {rows.length > 0 ? (
                    <Table<TingantaDerivativeArtha>
                      size="small"
                      rowKey="id"
                      loading={loading}
                      columns={derivativeArthaColumnsFor(kind)}
                      dataSource={rows}
                      pagination={false}
                      scroll={{ x: 640 }}
                    />
                  ) : (
                    <Typography.Paragraph type="secondary">
                      {loading ? 'Loading…' : 'none'}
                    </Typography.Paragraph>
                  )}
                </React.Fragment>
              );
            })}

            <Typography.Title level={5} style={{ marginTop: 16, marginBottom: 8 }}>
              Upasarga artha (prefix chains)
            </Typography.Title>
            <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
              <code>GET/POST /upasarga-artha</code> — meanings per language and ordered{' '}
              <code>upasargaIds</code>. Sequence matters: <code>[1, 4]</code> ≠ <code>[4, 1]</code>.
            </Typography.Paragraph>
            <Space style={{ marginBottom: 8 }}>
              <Button
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => openUaCreate(detail.id)}
              >
                Add upasarga artha
              </Button>
            </Space>
            {upasargaArthaRows.length > 0 ? (
              <Table<UpasargaArtha>
                size="small"
                rowKey="id"
                loading={upasargaArthasLoading}
                columns={upasargaArthaColumns}
                dataSource={upasargaArthaRows}
                pagination={false}
                scroll={{ x: 960 }}
              />
            ) : (
              <Typography.Paragraph type="secondary">
                {upasargaArthasLoading ? 'Loading…' : 'none'}
              </Typography.Paragraph>
            )}

            <TingantaRupSections tingantaId={detail.id} detail={detail} onReload={reloadDetailIfOpen} />

            <EntryMediaPanel
              variant="tinganta"
              parentId={detail.id}
              rawMedia={detail.media}
              onReload={reloadDetailIfOpen}
            />
          </Space>
        ) : null}
      </Modal>

      <Modal
        title={editing ? 'Edit tinganta' : 'Add tinganta'}
        open={modalOpen}
        onCancel={closeModal}
        onOk={() => void onSubmit()}
        confirmLoading={saving}
        destroyOnClose
        width={720}
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Typography.Title level={5} style={{ marginTop: 0 }}>
            Core
          </Typography.Title>
          <Form.Item name="i" label="i" rules={requiredStr('i')}>
            <Input placeholder="e.g. 1" />
          </Form.Item>
          <Form.Item name="baseindex" label="baseindex" rules={requiredStr('baseindex')}>
            <Input placeholder="e.g. 1.1" />
          </Form.Item>
          <Form.Item name="dhatu" label="dhātu" rules={requiredStr('dhātu')}>
            <Input placeholder="e.g. gam" />
          </Form.Item>
          <Form.Item name="aupadeshik" label="aupadeśik" rules={requiredStr('aupadeśik')}>
            <Input placeholder="e.g. गम्" />
          </Form.Item>
          <Form.Item name="gana" label="gaṇa" rules={requiredStr('gaṇa')}>
            <Input placeholder="e.g. bhvadi" />
          </Form.Item>
          <Form.Item name="pada" label="pada" rules={requiredStr('pada')}>
            <Input placeholder="e.g. parasmaipada" />
          </Form.Item>
          <Form.Item name="settva" label="settva" rules={requiredStr('settva')}>
            <Input placeholder="e.g. set" />
          </Form.Item>
          <Form.Item name="karma" label="karma" rules={requiredStr('karma')}>
            <Input placeholder="e.g. sakarmaka" />
          </Form.Item>
          <Form.Item name="artha" label="artha" rules={requiredStr('artha')}>
            <Input placeholder="e.g. to go" />
          </Form.Item>
          <Form.Item
            name="artha_english"
            label="artha_english"
            rules={requiredStr('artha_english')}
          >
            <Input placeholder="e.g. to go" />
          </Form.Item>

          <Typography.Title level={5}>Notes and references</Typography.Title>
          <Form.Item name="tags" label="tags">
            <Input placeholder="e.g. movement,verb" />
          </Form.Item>
          <Form.Item name="dhaturoopnandini" label="dhaturoopnandini">
            <Input placeholder="Reference" />
          </Form.Item>
          <Form.Item name="sk" label="sk">
            <Input />
          </Form.Item>
          <Form.Item name="lsk_sutra" label="lsk_sutra">
            <Input placeholder="Lsk sutra ref" />
          </Form.Item>
          <Form.Item name="sk_sutra" label="sk_sutra">
            <Input placeholder="Sk sutra ref" />
          </Form.Item>
          <Form.Item name="artha_hindi" label="artha_hindi">
            <Input placeholder="e.g. जाना" />
          </Form.Item>
          <Form.Item name="antarganas" label="antarganas">
            <Input placeholder="e.g. sub-gana" />
          </Form.Item>
          <Form.Item name="dhaturoopnandini_note" label="dhaturoopnandini_note">
            <Input.TextArea rows={2} placeholder="Note" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={taEditing ? 'Edit tinganta artha' : 'Add tinganta artha'}
        open={taModalOpen}
        onCancel={closeTaModal}
        onOk={() => void submitTaModal()}
        confirmLoading={taModalSaving}
        destroyOnClose
        width={520}
      >
        <Form form={taForm} layout="vertical" preserve={false}>
          <Form.Item
            name="languageId"
            label="Language"
            rules={[{ required: true, message: 'Language is required' }]}
          >
            <Select
              options={languageSelectOptions}
              showSearch
              optionFilterProp="label"
              placeholder="Select language"
            />
          </Form.Item>
          <Form.Item
            name="text"
            label="Text"
            rules={[{ required: true, message: 'Text is required' }]}
          >
            <Input.TextArea rows={4} placeholder="Meaning or translation" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={
          derArthaEditing
            ? `Edit — ${DERIVATIVE_ARTHA_SECTIONS[derArthaKind].title}`
            : `Add — ${DERIVATIVE_ARTHA_SECTIONS[derArthaKind].title}`
        }
        open={derArthaModalOpen}
        onCancel={closeDerArthaModal}
        onOk={() => void submitDerArthaModal()}
        confirmLoading={derArthaModalSaving}
        destroyOnClose
        width={520}
      >
        <Form form={derArthaForm} layout="vertical" preserve={false}>
          <Form.Item
            name="languageId"
            label="Language"
            rules={[{ required: true, message: 'Language is required' }]}
          >
            <Select
              options={languageSelectOptions}
              showSearch
              optionFilterProp="label"
              placeholder="Select language"
            />
          </Form.Item>
          <Form.Item
            name="text"
            label="Text"
            rules={[{ required: true, message: 'Text is required' }]}
          >
            <Input.TextArea rows={4} placeholder="Meaning or translation" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="View parsed"
        open={taParseModalOpen}
        onCancel={() => {
          if (!taParseSaving) closeTaParseView();
        }}
        closable={!taParseSaving}
        maskClosable={!taParseSaving}
        footer={
          taParsed ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 8,
              }}
            >
              <Checkbox
                checked={taParseDelete}
                disabled={taParseSaving}
                onChange={(e) => setTaParseDelete(e.target.checked)}
              >
                delete
              </Checkbox>
              <Space>
                <Button disabled={taParseSaving} onClick={closeTaParseView}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  loading={taParseSaving}
                  onClick={() => void handleTaParseDone()}
                >
                  Done
                </Button>
              </Space>
            </div>
          ) : null
        }
        width={640}
        destroyOnClose
      >
        {taParsed ? (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div>
              <Typography.Text strong>upasarga: </Typography.Text>
              <Typography.Text>{taParsed.upasarga || '—'}</Typography.Text>
              {taParsed.upasarga?.trim() ? (
                taParseUpasargaMatches.length > 0 ? (
                  <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                    — id{taParseUpasargaMatches.length > 1 ? 's' : ''}:{' '}
                    {taParseUpasargaMatches.map((u) => u.id).join(', ')}
                  </Typography.Text>
                ) : (
                  <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                    — (no matching row in loaded Upasarga list)
                  </Typography.Text>
                )
              ) : null}
            </div>
            <div>
              <Checkbox
                checked={taParseSelectDifferent}
                onChange={(e) => {
                  const on = e.target.checked;
                  setTaParseSelectDifferent(on);
                  if (!on) {
                    taParseExtraForm.resetFields();
                  }
                }}
              >
                Select different
              </Checkbox>
            </div>
            {taParseSelectDifferent ? (
              <Form form={taParseExtraForm} layout="vertical" preserve={false}>
                <Form.Item
                  label="Upasargas (order)"
                  extra="Add one row per prefix in attachment order (first row = outer/first). The same upasarga may appear multiple times (e.g. 1, 1, 1)."
                >
                  <Form.List name="manualUpasargaIds">
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map(({ key, name, ...restField }) => (
                          <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                            <Form.Item
                              {...restField}
                              name={name}
                              rules={[
                                { required: true, message: 'Select upasarga for this position' },
                              ]}
                              style={{ flex: 1, marginBottom: 0 }}
                            >
                              <Select
                                options={upasargaSelectOptions}
                                showSearch
                                optionFilterProp="label"
                                placeholder={`Position ${name + 1}`}
                                style={{ minWidth: 300 }}
                              />
                            </Form.Item>
                            <MinusCircleOutlined onClick={() => remove(name)} />
                          </Space>
                        ))}
                        <Form.Item style={{ marginBottom: 0 }}>
                          <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                            Add upasarga step
                          </Button>
                        </Form.Item>
                      </>
                    )}
                  </Form.List>
                </Form.Item>
              </Form>
            ) : null}
            <div>
              <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
                meanings:
              </Typography.Text>
              {taParsed.meanings.length > 0 ? (
                taParsed.meanings.map((m, i) => (
                  <Typography.Paragraph key={i} style={{ marginBottom: 8, whiteSpace: 'pre-wrap' }}>
                    {m}
                  </Typography.Paragraph>
                ))
              ) : (
                <Typography.Text type="secondary">(none)</Typography.Text>
              )}
            </div>
          </Space>
        ) : null}
      </Modal>

      <Modal
        title={uaEditing ? 'Edit upasarga artha' : 'Add upasarga artha'}
        open={uaModalOpen}
        onCancel={closeUaModal}
        onOk={() => void submitUaModal()}
        confirmLoading={uaModalSaving}
        destroyOnClose
        width={640}
        styles={{ body: { maxHeight: '75vh', overflowY: 'auto' } }}
      >
        <Form form={uaForm} layout="vertical" preserve={false}>
          <Form.Item
            name="languageId"
            label="Language"
            rules={[{ required: true, message: 'Language is required' }]}
          >
            <Select
              options={languageSelectOptions}
              showSearch
              optionFilterProp="label"
              placeholder="Select language"
            />
          </Form.Item>
          <Form.Item
            label="Upasargas (order)"
            extra="Add one row per prefix in attachment order (first row = outer/first). The same upasarga may appear multiple times (e.g. 1, 1, 1)."
          >
            <Form.List
              name="upasargaIds"
              rules={[
                {
                  validator: async (_rule, value) => {
                    if (!value || value.length < 1) {
                      throw new Error('Add at least one upasarga in order');
                    }
                  },
                },
              ]}
            >
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                      <Form.Item
                        {...restField}
                        name={name}
                        rules={[{ required: true, message: 'Select upasarga for this position' }]}
                        style={{ flex: 1, marginBottom: 0 }}
                      >
                        <Select
                          options={upasargaSelectOptions}
                          showSearch
                          optionFilterProp="label"
                          placeholder={`Position ${name + 1}`}
                          style={{ minWidth: 300 }}
                        />
                      </Form.Item>
                      <MinusCircleOutlined onClick={() => remove(name)} />
                    </Space>
                  ))}
                  <Form.Item style={{ marginBottom: 0 }}>
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                      Add upasarga step
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Form.Item>
          <Form.Item
            name="artha"
            label="Artha"
            rules={[{ required: true, message: 'Artha is required' }]}
          >
            <Input.TextArea rows={3} placeholder="Meaning text" />
          </Form.Item>
          <Form.Item name="example" label="Example">
            <Input placeholder="Optional" />
          </Form.Item>
          <Form.Item name="antonyms" label="Antonyms">
            <Input placeholder="Optional" />
          </Form.Item>
          <Form.Item name="synonyms" label="Synonyms">
            <Input placeholder="Optional" />
          </Form.Item>
          <Form.Item name="tags" label="Tags">
            <Input placeholder="Optional" />
          </Form.Item>
          <Form.Item name="bidha" label="Bidha">
            <Select
              allowClear
              options={bidhaOpts}
              showSearch
              optionFilterProp="label"
              placeholder="Optional"
            />
          </Form.Item>
          <Form.Item name="prakar" label="Prakār (śreṇi)">
            <Select
              allowClear
              options={prakarOpts}
              showSearch
              optionFilterProp="label"
              placeholder="Optional"
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
